import axios from "axios";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryCache } from "react-query";
import { useSelector } from "react-redux";
import { MomentLocale } from "src/localization/momentLocale";
import {
  handleInfoToast,
  handleSucces,
  safeQueryBuilder,
  searchQueryBuilder,
} from "src/utils/formUtils";
import {
  formatFilter,
  tableBasicParams,
  tableBasicParamsWithRow,
} from "src/utils/tableUtils";
import {
  deleteEle,
  get,
  getBaseUrl,
  getInventory,
  patch,
  post,
  isShownConcurrencyFailure,
  showConcurrencyFailure,
} from "./dataService";
import {
  ACCOUNT_DELETE_API,
  ANAYLYTIC_SALES_TREND_API,
  ANAYLYTIC_SALES_YEARLY_TREND_API,
  BANK_API,
  BANK_SYNC_API,
  CATEGORY_API,
  CLOSING_REPORT,
  DELETE_FILE,
  DETAIL_TRANSACTION_ACCOUNT_REPORT,
  DOWNLOAD_INVOICES,
  DOWNLOAD_TEMPLATE,
  FETCH_FILE,
  INVENTORY_API,
  KEY_VALUE_FETCH_ALL_API,
  ORDER_FETCH_ALL_API,
  ORDER_GENERATE_INVOICE,
  PARTNER_API,
  PARTNER_GROUP_API,
  PAYMENT_TRANSACTION_CREATE_API,
  PAYMENT_TRANSACTION_DELETE_API,
  PAYMENT_TRANSACTION_FETCH_ALL_API,
  PAYMENT_TRANSACTION_SUMMARY,
  PAYMENT_TRANSACTION_UPDATE_API,
  PRODUCT_API,
  PRODUCT_CONCILIATION_FETCH_ALL_API,
  PRODUCT_CONCILIATION_FETCH_API,
  PRODUCT_FETCH_BRAND_CATEGORY_API,
  PRODUCT_SKU_BARCODE_API,
  PROJECT_API,
  RECURRING_UPDATE_API,
  REPORT_BALANCE_SHEET,
  REPORT_COA_BREAKDOWN,
  REPORT_CASHFLOW,
  REPORT_CASHFLOW_STATEMENT,
  REPORT_CLOSEOUT_REPORT,
  REPORT_INCOME_STATEMENT,
  REPORT_LEDGER,
  REPORT_STAFF_PERFORMANCE,
  REPORT_STAFF_PERFORMANCE_DETAILS,
  REPORT_TRIAL_BALANCE,
  SETTING_CREATE_API,
  SETTING_FETCH_ALL_API,
  SETTING_UPDATE_API,
  TRANSACTION_API,
  TRANSACTION_DELETE_API,
  TRANSACTION_FETCH_API,
  TRANSACTION_GENERATE_QUOTATION_API,
  TRANSACTION_NEW_API,
  TRANSACTION_UPDATE_API,
  UPLOAD_TEMPLATE,
  USER_CURRENT_API,
} from "./pathRequest";
var FileSaver = require("file-saver");

export const DeleteAccount = () => {
  const queryCache = useQueryCache();
  const { t } = useTranslation();
  return useMutation(() =>
    deleteEle({
      path: ACCOUNT_DELETE_API,
    })
  );
};

export const UserInfo = (callback) => CompanyInfo(callback);

export const CompanyInfo = (callback) =>
  useQuery(
    ["USER_CURRENT_API"],
    () =>
      get({
        path: USER_CURRENT_API,
      }),
    {
      keepPreviousData: true,
      cacheTime: 600000,
      staleTime: 600000,
      retry: false,
      onSuccess: (response) => {
        if (callback) {
          callback(response);
        }
      },
    }
  );

export const ProjectList = (callback) => {
  const projects = useSelector((state) => state?.appContext);
  return useQuery(
    ["PROJECT_LIST"],
    () =>
      get({
        path: searchQueryBuilder(
          safeQueryBuilder(
            PROJECT_API,
            tableBasicParamsWithRow(0, "createdDate", "false", 1000)
          ),
          [
            { key: "configType", operation: ":", value: "PROJECT" },
            { key: "id", operation: ":", value: formatFilter(projects) },
          ]
        ),
      }),
    {
      onSuccess: (response) => {
        if (callback) {
          callback(response);
        }
      },
      refetchOnWindowFocus: false,
    }
  );
};

export const ProjectListMutate = () => {
  return useMutation((search) =>
    get({
      path: searchQueryBuilder(
        safeQueryBuilder(
          PROJECT_API,
          tableBasicParamsWithRow(0, "createdDate", "false", 1000)
        ),
        [
          { key: "configType", operation: ":", value: "PROJECT" },
          { key: "projectTitle", operation: "~", value: search },
        ]
      ),
    })
  );
};

export const CategoryListMutate = (extra) =>
  useMutation((search) =>
    get({
      path: searchQueryBuilder(
        safeQueryBuilder(
          CATEGORY_API,
          tableBasicParamsWithRow(0, "createdDate", "false", 1000)
        ),
        [{ key: "category", operation: "~", value: search }].concat(extra ?? [])
      ),
    })
  );

/**
 * Every account on a project, for a picker -- not a page of them.
 *
 * `tableBasicParams` leaves the row count at the server default of 10, which is a table's
 * page size, not a dropdown's. A project with more than ten accounts silently lost the rest:
 * the deposit form's category list went empty the moment the ten most recently created
 * accounts happened not to include a deposit category, with nothing on screen to say the
 * categories existed but were on page two.
 */
export const CategoryListByProject = ({ page, sortBy, isAsc, projectId }) =>
  useQuery(["CATEGORY_API", { page, sortBy, isAsc, projectId }], () =>
    get({
      path: searchQueryBuilder(
        safeQueryBuilder(
          CATEGORY_API,
          tableBasicParamsWithRow(page, sortBy, isAsc, 1000)
        ),
        [
          {
            key: "project",
            operation: ":",
            value: projectId?.replaceAll("-", "DasH"),
          },
        ]
      ),
    })
  );

export const CategoryListByProjectMutate = (search, extra) =>
  useMutation(
    (projectId) =>
      get({
        path: searchQueryBuilder(
          safeQueryBuilder(
            CATEGORY_API,
            tableBasicParamsWithRow(0, "createdDate", "false", 1000)
          ),
          [
            {
              key: "project",
              operation: ":",
              value:
                search?.replaceAll("-", "DasH") ||
                projectId?.replaceAll("-", "DasH"),
            },
          ].concat(extra ?? [])
        ),
      }),
    {
      refetchOnWindowFocus: false,
      staleTime: "Infinity",
    }
  );

export const BankList = () =>
  useQuery(
    "BANK_API",
    () =>
      get({
        path: BANK_API,
      }),
    {
      refetchOnWindowFocus: false,
    }
  );

export const CreateTransaction = (callback) => {
  const queryCache = useQueryCache();
  const { t } = useTranslation();
  return useMutation(
    (form) =>
      post({
        path: TRANSACTION_NEW_API,
        formData: form,
      }),
    {
      onSuccess: () => {
        if (callback) {
          callback();
        } else {
          queryCache.refetchQueries("INVOICE_API", { lazy: true });
          queryCache.invalidateQueries("BANK_API", { lazy: true });
          handleSucces(t("saved"), t("success"));
        }
      },

      onError: (error) => {
        if (isShownConcurrencyFailure(error)) return;
        var errorBody = error?.response?.data?.response;
        var message = errorBody?.status?.httpMessage;
        handleInfoToast(
          message || "Transaction was not saved, please try again"
        );
      },
    }
  );
};

export const GenerateQuotation = (callback) => {
  const queryCache = useQueryCache();
  const { t } = useTranslation();
  return useMutation(
    (form) =>
      post({
        path: TRANSACTION_GENERATE_QUOTATION_API,
        formData: form,
      }),
    {
      onSuccess: () => {
        if (callback) {
          callback();
        } else {
          queryCache.refetchQueries("INVOICE_API", { lazy: true });
          queryCache.invalidateQueries("BANK_API", { lazy: true });
          handleSucces(t("saved"), t("success"));
        }
      },
    }
  );
};

export const GetTransaction = (transactionId, callBack = null) => {
  if (!transactionId) {
    return { data: null };
  }
  return useQuery(
    ["TRANSACTION_FETCH_API", transactionId],
    () =>
      get({
        path: safeQueryBuilder(TRANSACTION_FETCH_API, { id: transactionId }),
      }),
    {
      onSuccess: (transactionInfo) => {
        if (callBack) callBack(transactionInfo);
      },
      refetchOnWindowFocus: false,
    }
  );
};

export const UpdateTransaction = (callback) =>
  useMutation(
    (form) =>
      patch({
        path: TRANSACTION_UPDATE_API,
        formData: form,
      }),
    {
      onSuccess: () => {
        callback();
      },

      // Without this a rejected update was silent: react-query resolves mutate() with
      // undefined rather than throwing, so the POS screen carried on to its success path and
      // a payment the server refused still looked taken.
      onError: (error) => {
        if (isShownConcurrencyFailure(error)) return;
        var errorBody = error?.response?.data?.response;
        var message = errorBody?.status?.httpMessage;
        handleInfoToast(
          message || "Transaction was not saved, please try again"
        );
      },
    }
  );

export const DeleteTransaction = (transactionId) =>
  useMutation(() =>
    deleteEle({
      path: safeQueryBuilder(TRANSACTION_DELETE_API, { id: transactionId }),
    })
  );

export const PaymentTransactionList = ({
  page,
  sortBy,
  isAsc,
  startDateFilter,
  endDateFilter,
  transactionId,
  type = "transaction",
  extra,
  row = 10,
  execute = true,
}) =>
  useQuery(
    [
      "PAYMENT_TRANSACTION_FETCH_ALL_API",
      {
        page,
        sortBy,
        isAsc,
        startDateFilter,
        endDateFilter,
        transactionId,
        ...extra,
      },
    ],
    () => {
      if (!execute) return Promise.resolve();

      return get({
        path: searchQueryBuilder(
          safeQueryBuilder(
            PAYMENT_TRANSACTION_FETCH_ALL_API,
            Object.assign(tableBasicParamsWithRow(page, sortBy, isAsc, row), {
              id: transactionId,
              type: type,
            })
          ),
          [
            { key: "paymentDate", operation: ">=", value: startDateFilter },
            { key: "paymentDate", operation: "<=", value: endDateFilter },
          ].concat(extra ?? [])
        ),
      });
    }
  );

export const CreatePaymentTransaction = (callback) =>
  useMutation(
    (form) =>
      post({
        path: PAYMENT_TRANSACTION_CREATE_API,
        formData: form,
      }),
    {
      onSuccess: () => {
        callback();
      },
    }
  );

export const UpdatePaymentTransaction = (callback) =>
  useMutation(
    (form) =>
      patch({
        path: PAYMENT_TRANSACTION_UPDATE_API,
        formData: form,
      }),
    {
      onSuccess: () => {
        callback();
      },
    }
  );

export const DeletePayment = () => {
  const queryCache = useQueryCache();
  const { t } = useTranslation();
  return useMutation(
    (paymentTransactionId) =>
      deleteEle({
        path: safeQueryBuilder(PAYMENT_TRANSACTION_DELETE_API, {
          id: paymentTransactionId,
        }),
      }),
    {
      onSuccess: () => {
        queryCache.refetchQueries("PAYMENT_TRANSACTION_FETCH_ALL_API", {
          lazy: true,
        });
        queryCache.invalidateQueries("TRANSACTION_FETCH_API", { lazy: true });
        queryCache.refetchQueries("TRANSACTION_API", { lazy: true });
        handleSucces(t("saved"), t("success"));
      },
    }
  );
};

export const GetBankList = () => {
  const projects = useSelector((state) => state?.appContext);
  return useQuery(
    "BANK_API",
    () =>
      get({
        path: searchQueryBuilder(
          safeQueryBuilder(
            BANK_API,
            tableBasicParamsWithRow(0, "name", true, 50)
          ),
          [{ key: "project", operation: ":", value: formatFilter(projects) }]
        ),
      }),
    {
      refetchOnWindowFocus: false,
    }
  );
};

export const BankSync = () => {
  const queryCache = useQueryCache();
  return useMutation(
    () =>
      get({
        path: BANK_SYNC_API,
      }),
    {
      onSuccess: () => {
        queryCache.invalidateQueries("BANK_API", { lazy: true });
      },
    }
  );
};

// companyName is matched alongside name because business partners are commonly filed with the
// trading name in companyName and a contact person (or nothing) in name -- searching name only
// meant those partners never came back from the picker at all.
export const SearchCustomerMutate = (type) =>
  useMutation((search) =>
    get({
      path: searchQueryBuilder(
        safeQueryBuilder(PARTNER_API, tableBasicParams(1, "name", false)),
        [
          { key: "email", operation: "~", value: search },
          { key: "telNo", operation: ":", value: search },
          { key: "name", operation: "~", value: search },
          { key: "companyName", operation: "~", value: search },
          { key: "type", operation: ":", value: type },
        ]
      ),
    })
  );

export const SearchStaffMutate = () =>
  useMutation((search) =>
    get({
      path: searchQueryBuilder(
        safeQueryBuilder(PARTNER_API, tableBasicParams(1, "name", false)),
        [
          { key: "email", operation: "~", value: search },
          { key: "telNo", operation: "~", value: search },
          { key: "name", operation: "~", value: search },
          { key: "type", operation: ":", value: "STAFF" },
        ]
      ),
    })
  );

export const PartnerGroupList = (callback) =>
  useQuery(
    ["GROUP_API"],
    () =>
      get({
        path: PARTNER_GROUP_API,
      }),
    {
      onSuccess: (response) => callback(response),
      refetchOnWindowFocus: false,
    }
  );

export const SearchProduct = (searchBy = "name") =>
  useMutation((search) =>
    getInventory({
      path: searchQueryBuilder(
        safeQueryBuilder(PRODUCT_API, tableBasicParams(1, searchBy, false)),
        [
          {
            key: searchBy,
            operation: "~",
            value: search ? formatFilter(search) : " ",
          },
        ]
      ),
    })
  );

export const SearchProductByNameAndSku = () =>
  useMutation((search) =>
    getInventory({
      path: searchQueryBuilder(
        safeQueryBuilder(PRODUCT_API, tableBasicParams(1, "name", false)),
        [
          {
            key: "name",
            operation: "~",
            value: search ? formatFilter(search) : " ",
          },
          {
            key: "sku",
            operation: "~",
            value: search ? formatFilter(search) : " ",
          },
        ]
      ),
    })
  );
export const SearchProductByBarcode = () =>
  useMutation((search) =>
    getInventory({
      path: searchQueryBuilder(
        safeQueryBuilder(
          PRODUCT_SKU_BARCODE_API,
          tableBasicParams(1, "barcodeId", false)
        ),
        [
          {
            key: "barcodeId",
            operation: ":",
            value: search ? search.replaceAll(" ", "SpAcE") : " ",
          },
        ]
      ),
    })
  );

export const Print = (url) => {
  const { t } = useTranslation();
  return useMutation(
    (form) =>
      axios.post(url, form, {
        timeout: 2000,
      }),
    {
      onSuccess: () => {
        handleSucces("Print " + t("success"), t("success"));
      },
    }
  );
};

export const GenerateCustomerLedger = () =>
  useMutation(({ projectId, startDate, endDate, filter, partnerId }) =>
    get({
      path: safeQueryBuilder(REPORT_LEDGER, {
        projectId: projectId,
        startDate: startDate,
        endDate: endDate,
        ledgerType: "customerLedger",
        filter: filter,
        // Set only by the statement of account, which is about one partner. Omitted, the
        // report covers every partner as it always has.
        partnerId: partnerId,
      }),
    })
  );

export const GenerateClosingReport = () =>
  useMutation(({ projectId, startDate, endDate }) =>
    get({
      path: safeQueryBuilder(CLOSING_REPORT, {
        projectId: projectId,
        startDate: startDate,
        endDate: endDate,
      }),
    })
  );

export const GenerateDetailTransactionAccountReport = () =>
  useMutation(({ projectId, startDate, endDate }) =>
    get({
      path: safeQueryBuilder(DETAIL_TRANSACTION_ACCOUNT_REPORT, {
        projectId: projectId,
        startDate: startDate,
        endDate: endDate,
      }),
    })
  );

export const GenerateReportStaffPerformance = () =>
  useMutation(({ projectId, startDate, endDate, self }) =>
    get({
      path: safeQueryBuilder(REPORT_STAFF_PERFORMANCE, {
        projectId: projectId,
        startDate: startDate,
        endDate: endDate,
        self: self,
      }),
    })
  );

export const GenerateReportStaffPerformanceDetails = () =>
  useMutation(({ projectId, startDate, endDate, staffId }) =>
    get({
      path: safeQueryBuilder(REPORT_STAFF_PERFORMANCE_DETAILS, {
        projectId: projectId,
        startDate: startDate,
        endDate: endDate,
        staffId: staffId,
      }),
    })
  );

export const GenerateSupplierLedger = () =>
  useMutation(({ projectId, startDate, endDate, filter, partnerId }) =>
    get({
      path: safeQueryBuilder(REPORT_LEDGER, {
        projectId: projectId,
        startDate: startDate,
        endDate: endDate,
        ledgerType: "supplierLedger",
        filter: filter,
        // Set only by the statement of account, which is about one partner. Omitted, the
        // report covers every partner as it always has.
        partnerId: partnerId,
      }),
    })
  );

export const GenerateReportIncomeStatement = () =>
  useMutation(({ projectId, startDate, endDate, cogsDirectPurchase }) =>
    get({
      path: safeQueryBuilder(REPORT_INCOME_STATEMENT, {
        projectId: projectId,
        startDate: startDate,
        endDate: endDate,
        cogsDirectPurchase: cogsDirectPurchase,
      }),
    })
  );

export const GenerateReportCashflow = () =>
  useMutation(({ projectId, startDate, endDate, cogsDirectPurchase }) =>
    get({
      path: safeQueryBuilder(REPORT_CASHFLOW, {
        projectId: projectId,
        startDate: startDate,
        endDate: endDate,
        cogsDirectPurchase: cogsDirectPurchase,
      }),
    })
  );

/**
 * Both methods of the statement of cash flows, grouped into operating, investing and financing.
 *
 * Separate from GenerateReportCashflow, which is the older flat, unclassified view of the same
 * working-capital groups. Both endpoints close on the same net cash figure.
 */
export const GenerateReportCashflowStatement = () =>
  useMutation(({ projectId, startDate, endDate, cogsDirectPurchase }) =>
    get({
      path: safeQueryBuilder(REPORT_CASHFLOW_STATEMENT, {
        projectId: projectId,
        startDate: startDate,
        endDate: endDate,
        cogsDirectPurchase: cogsDirectPurchase,
      }),
    })
  );

export const GenerateReportTrialBalance = () =>
  useMutation(({ projectId, startDate, endDate, cogsDirectPurchase }) =>
    get({
      path: safeQueryBuilder(REPORT_TRIAL_BALANCE, {
        projectId: projectId,
        startDate: startDate,
        endDate: endDate,
        cogsDirectPurchase: cogsDirectPurchase,
      }),
    })
  );

export const GenerateReportBalanceSheet = () =>
  useMutation(({ projectId, startDate, endDate, cogsDirectPurchase }) =>
    get({
      path: safeQueryBuilder(REPORT_BALANCE_SHEET, {
        projectId: projectId,
        startDate: startDate,
        endDate: endDate,
        cogsDirectPurchase: cogsDirectPurchase,
      }),
    })
  );

/**
 * The chart of accounts behind the statements, for the same period.
 *
 * Read alongside the five statements rather than folded into them. The statements answer "what
 * were the totals"; this answers "which accounts made them up", including the accounts that made
 * up nothing -- which the statements cannot report, because they group transactions and an idle
 * account has none to group.
 *
 * No cogsDirectPurchase: the breakdown reports what each account was billed, and that flag picks
 * between two ways of deriving one aggregate line.
 */
export const GenerateReportCoaBreakdown = () =>
  useMutation(({ projectId, startDate, endDate }) =>
    get({
      path: safeQueryBuilder(REPORT_COA_BREAKDOWN, {
        projectId: projectId,
        startDate: startDate,
        endDate: endDate,
      }),
    })
  );

export const OrderGenerateInvoice = () =>
  useMutation((id) =>
    get({
      path: safeQueryBuilder(ORDER_GENERATE_INVOICE, { id: id }),
    })
  );

export const GetOrder = ({
  page,
  sortBy,
  isAsc,
  startDateFilter,
  endDateFilter,
  orderType,
  filterStatusType = "status",
  statusType,
}) =>
  useQuery(
    [
      "ORDER_FETCH_ALL_API",
      {
        page,
        sortBy,
        isAsc,
        startDateFilter,
        endDateFilter,
        orderType,
        filterStatusType,
        statusType,
      },
    ],
    () =>
      get({
        path: searchQueryBuilder(
          safeQueryBuilder(
            ORDER_FETCH_ALL_API,
            tableBasicParams(page, sortBy, isAsc)
          ),
          [
            { key: "createdDate", operation: ">=", value: startDateFilter },
            { key: "createdDate", operation: "<=", value: endDateFilter },
            { key: "type", operation: ":", value: orderType },
            { key: filterStatusType, operation: ":", value: statusType },
          ]
        ),
      }),
    {
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: "Infinity",
    }
  );

export const OrderSetting = (callback) => SettingConfig(callback);

export const SettingConfig = (callback) =>
  useQuery(
    "SETTING_FETCH_ALL_API",
    () =>
      get({
        path: safeQueryBuilder(SETTING_FETCH_ALL_API),
      }),
    {
      onSuccess: (data) => callback(data),
      refetchOnWindowFocus: false,
    }
  );

/**
 * The APP_SETTING row's config, parsed.
 *
 * <p>The server stores it as a JSON STRING in a single column, so every reader has to parse it
 * for itself. Doing that in three places is how {@link SettingConfig} callers end up disagreeing
 * about the shape — one of them reading `setting.config.someFlag` off the raw string and quietly
 * getting `undefined` forever. This is the one place that parse lives.
 *
 * <p>Shares react-query's "SETTING_FETCH_ALL_API" key with {@link SettingConfig}, so a screen
 * using both makes one request.
 *
 * <p>A config that will not parse yields no settings rather than throwing. The alternative is a
 * white screen on a single malformed row, and every consumer here is a feature FLAG whose safe
 * reading is "off".
 */
export const useAppSettingConfig = () => {
  const { data } = useQuery(
    "SETTING_FETCH_ALL_API",
    () =>
      get({
        path: safeQueryBuilder(SETTING_FETCH_ALL_API),
      }),
    { refetchOnWindowFocus: false }
  );

  const setting = data?.data?.result?.filter(
    (row) => row?.type === "APP_SETTING"
  )?.[0];

  return parseSettingConfig(setting?.config);
};

/** Shared by the reader above and by the settings form that writes the row. */
export const parseSettingConfig = (config) => {
  if (!config) return undefined;
  if (typeof config === "object") return config;
  try {
    return JSON.parse(config);
  } catch (e) {
    return undefined;
  }
};

/**
 * Whether e-Invoice submission to LHDN is switched on for this business.
 *
 * <p>Off unless someone has turned it on, and deliberately so: transmission to MyInvois is not
 * finished, and a control that reports a document to the regulator is not something to leave
 * live by default. While it is off, every submission control and every consolidation-deadline
 * warning is withheld — a deadline for a submission the operator cannot make is noise, and worse
 * than noise, because it implies an obligation the software is not yet meeting.
 */
export const useLhdnSubmissionEnabled = () =>
  !!useAppSettingConfig()?.enableLhdnSubmission;

/** The two invoice form themes. `classic` is the form every existing user already knows. */
export const INVOICE_FORM_THEMES = { CLASSIC: "classic", MODERN: "modern" };

/**
 * Which invoice form to render — the long-standing CoreUI form, or the document-style one.
 *
 * <p>Classic unless the business has chosen otherwise, and that default is the whole point: the
 * bill forms are the screens this product's users touch most, and swapping the layout under
 * someone who did not ask for it costs them their muscle memory on the one screen they cannot
 * afford to be slow on. An unset flag, an unparseable config row and a value nobody recognises
 * all land on classic for the same reason.
 *
 * <p>The two themes render the SAME Formik form — same initial values, same yup schema, same
 * submit. Only the body differs, so a business can switch back and forth without any bill
 * behaving differently for having been written under one theme or the other.
 */
export const useInvoiceFormTheme = () => {
  const theme = useAppSettingConfig()?.invoiceFormTheme;
  return theme === INVOICE_FORM_THEMES.MODERN
    ? INVOICE_FORM_THEMES.MODERN
    : INVOICE_FORM_THEMES.CLASSIC;
};

export const CreateSetting = (callback) =>
  useMutation(
    (form) =>
      post({
        path: SETTING_CREATE_API,
        formData: form,
      }),
    {
      onSuccess: () => callback(),
    }
  );

export const UpdateSetting = (callback) =>
  useMutation(
    (form) =>
      patch({
        path: SETTING_UPDATE_API,
        formData: form,
      }),
    {
      onSuccess: () => callback(),
    }
  );

export const InventoryList = ({ page, sortBy, isAsc }) => {
  const projects = useSelector((state) => state?.appContext);
  return useQuery(
    ["INVENTORY_API", { page, sortBy, isAsc }],
    () =>
      getInventory({
        path: searchQueryBuilder(
          safeQueryBuilder(
            INVENTORY_API,
            tableBasicParams(page, sortBy, isAsc)
          ),
          [
            {
              key: "extraReference",
              operation: ":",
              value: formatFilter(projects),
            },
          ]
        ),
      }),
    {
      retry: false,
      refetchOnWindowFocus: false,
    }
  );
};

export const ProductBrandCategory = () => {
  return useQuery(
    ["PRODUCT_FETCH_BRAND_CATEGORY_API"],
    () =>
      getInventory({
        path: PRODUCT_FETCH_BRAND_CATEGORY_API,
      }),
    {
      retry: false,
      refetchOnWindowFocus: false,
    }
  );
};

export const InventoryListMutate = () => {
  const projects = useSelector((state) => state?.appContext);
  return useMutation(
    (search) =>
      getInventory({
        path: searchQueryBuilder(
          INVENTORY_API,
          [
            {
              key: "extraReference",
              operation: ":",
              value: formatFilter(projects),
            },
          ].concat(search)
        ),
      }),
    {
      retry: false,
    }
  );
};

export const ProductCategoryMutate = () =>
  useMutation(
    (search) => {
      return getInventory({
        path: searchQueryBuilder(KEY_VALUE_FETCH_ALL_API, [
          { key: "klass", operation: ":", value: "InvProductEntity" },
          { key: "label", operation: "~", value: search },
        ]),
      });
    },
    {
      refetchOnWindowFocus: false,
      staleTime: "Infinity",
    }
  );

export const ConciliationList = ({ productId, page, sortBy, isAsc }) =>
  useQuery(
    ["PRODUCT_CONCILIATION_FETCH_ALL_API", { productId, page, sortBy, isAsc }],
    () =>
      getInventory({
        path: searchQueryBuilder(
          safeQueryBuilder(
            PRODUCT_CONCILIATION_FETCH_ALL_API,
            tableBasicParams(page, sortBy, isAsc)
          ),
          [
            {
              key: "product",
              operation: ":",
              value: productId.replaceAll("-", "DasH"),
            },
          ]
        ),
      })
  );

export const GetConciliation = (invProductConciliationId) =>
  useQuery("PRODUCT_CONCILIATION_FETCH_API", () =>
    getInventory({
      path: PRODUCT_CONCILIATION_FETCH_API + "?id=" + invProductConciliationId,
    })
  );

export const DownloadTransactionTemplate = () => {
  var base = getBaseUrl("eas");

  return useMutation(() =>
    axios
      .get(base + DOWNLOAD_TEMPLATE, {
        responseType: "blob",
      })
      .then((response) => {
        const blob = new Blob([response.data]);
        FileSaver.saveAs(blob, "transaction-template.csv");
      })
  );
};

export const UploadTransactionTemplate = (url) => {
  const queryCache = useQueryCache();
  const { t } = useTranslation();
  var base = getBaseUrl("eas");
  const instance = axios.create();

  return useMutation(
    (form) =>
      instance.post(base + (url ?? UPLOAD_TEMPLATE), form, { timeout: 600000 }),
    {
      onSuccess: () => {
        handleSucces(t("saved"), t("success"));
        queryCache.invalidateQueries("CATEGORY_API", { lazy: true });
        queryCache.invalidateQueries("TRANSACTION_API", { lazy: true });
        queryCache.invalidateQueries("PARTNER_API", { lazy: true });
      },

      onError: (error) => {
        if (showConcurrencyFailure(error)) return;
        var errorBody = error?.response?.data?.response;
        var message = errorBody?.status?.httpMessage;
        handleInfoToast(message);
      },
    }
  );
};

export const UploadImage = (url) => {
  const { t } = useTranslation();
  var base = getBaseUrl("eas");
  const instance = axios.create();

  return useMutation((form) => instance.patch(base + url, form), {
    onSuccess: () => {
      handleSucces(t("saved"), t("success"));
    },

    onError: (error) => {
      if (showConcurrencyFailure(error)) return;
      var errorBody = error?.response?.data?.response;
      var message = errorBody?.status?.httpMessage;
      handleInfoToast(message);
    },
  });
};

export const DownloadInvoices = () => {
  var base = getBaseUrl("eas");
  const moment = MomentLocale();

  return useMutation(({ projectId, startDate, endDate }) =>
    axios
      .get(
        safeQueryBuilder(base + DOWNLOAD_INVOICES, {
          projectId: projectId,
          startDate: startDate,
          endDate: endDate,
        }),
        {
          responseType: "blob",
        }
      )
      .then((response) => {
        var name = `invoice-${moment().format("YYYY-MM-DD")}.csv`;
        const blob = new Blob([response.data]);
        FileSaver.saveAs(blob, name);
      })
  );
};

export const UploadFile = (url) => {
  const queryCache = useQueryCache();
  const { t } = useTranslation();
  var base = getBaseUrl("eas");
  const instance = axios.create();

  return useMutation(
    (form) => instance.post(base + url, form, { timeout: 600000 }),
    {
      onSuccess: () => {
        handleSucces(t("saved upload attachment"), t("success"));
        queryCache.invalidateQueries("CATEGORY_API", { lazy: true });
        queryCache.refetchQueries("PAYMENT_TRANSACTION_FETCH_ALL_API", {
          lazy: true,
        });
        queryCache.refetchQueries("PAYMENT_TRANSACTION_FETCH_API", {
          lazy: true,
        });
        queryCache.refetchQueries("TRANSACTION_API", { lazy: true });
        queryCache.refetchQueries("TRANSACTION_FETCH_API", { lazy: true });
      },

      onError: (error) => {
        if (showConcurrencyFailure(error)) return;
        var errorBody = error?.response?.data?.response;
        var message = errorBody?.status?.httpMessage;
        handleInfoToast(message);
      },
    }
  );
};

export const DownloadFile = () => {
  var base = getBaseUrl("eas");
  const moment = MomentLocale();

  return useMutation((fileId) =>
    axios
      .get(safeQueryBuilder(base + FETCH_FILE + "?id=" + fileId), {
        responseType: "blob",
      })
      .then((response) => {
        let filename = response.headers["content-disposition"]
          .split("filename=")[1]
          .split(".")[0];
        let e = response.headers["content-disposition"].split(".");
        let extension = e[e.length - 1].split(";")[0];
        var name = `${filename}.${extension}`;
        const blob = new Blob([response.data]);
        FileSaver.saveAs(blob, name);
      })
      .finally(() => {
        // console.log("finish");
      })
  );
};

export const DeleteFile = () =>
  useMutation((fileId) =>
    deleteEle({
      path: safeQueryBuilder(DELETE_FILE, { id: fileId }),
    })
  );

export const UpdateRecurring = () =>
  useMutation((form) =>
    patch({
      path: RECURRING_UPDATE_API,
      formData: form,
    })
  );

export const SettingListByType = ({ page, sortBy, isAsc, type }) =>
  useQuery(
    ["SETTING_FETCH_ALL_API", { page, sortBy, isAsc, type: type }],
    () =>
      get({
        path: searchQueryBuilder(
          safeQueryBuilder(
            SETTING_FETCH_ALL_API,
            tableBasicParams(page, sortBy, isAsc)
          ),
          [{ key: "type", operation: ":", value: type }]
        ),
      }),
    {
      refetchOnWindowFocus: false,
    }
  );

export const PartnerTransactionList = (
  cacheKey,
  { page, sortBy, isAsc, startDateFilter, endDateFilter, partnerId, extra = [] }
) =>
  useQuery(
    [
      cacheKey,
      {
        page,
        sortBy,
        isAsc,
        startDateFilter,
        endDateFilter,
        partnerId,
        ...extra,
      },
    ],
    () =>
      get({
        path: searchQueryBuilder(
          safeQueryBuilder(
            TRANSACTION_API,
            tableBasicParams(page, sortBy, isAsc)
          ),
          [
            {
              key: "partner",
              operation: ":",
              value: partnerId.replaceAll("-", "DasH"),
            },
            {
              key: "type",
              operation: ":",
              value: "INVOICE",
            },
          ].concat(extra ?? [])
        ),
      })
  );

export const GenerateReportCloseout = () =>
  useMutation(({ projectId, startDate, endDate }) =>
    get({
      path: safeQueryBuilder(REPORT_CLOSEOUT_REPORT, {
        projectId: projectId,
        startDate: startDate,
        endDate: endDate,
      }),
    })
  );

export const GenerateSalesTrend = () =>
  useMutation(({ projectId, startDate, endDate }) =>
    get({
      path: safeQueryBuilder(ANAYLYTIC_SALES_TREND_API, {
        projectId: projectId,
        startDate: startDate,
        endDate: endDate,
      }),
    })
  );

export const GenerateSalesYearlyTrend = () =>
  useMutation(({ projectId, startDate, endDate }) =>
    get({
      path: safeQueryBuilder(ANAYLYTIC_SALES_YEARLY_TREND_API, {
        projectId: projectId,
        startDate: startDate,
        endDate: endDate,
      }),
    })
  );

export const GetPaymentSummary = () =>
  useMutation((search) =>
    get({
      path: searchQueryBuilder(PAYMENT_TRANSACTION_SUMMARY, search),
    })
  );
